/**
 * platformToolLabelsData.ts
 *
 * Pure-data registry of platform tool keys to human-friendly display labels.
 * This file is client-bundle safe and imports NO code—only data for use in
 * UI labeling, avoiding Next.js hybrid import errors.
 */

export const PLATFORM_TOOL_LABELS: Record<string, string> = {
  // --- Web & Utility ---
  webScraper__googleSearch: 'Web search',
  webScraper__getWeather: 'Weather',
  webScraper__FetchDataFromUrl: 'Fetch URL',
  webScraper__youtubeCaptions: 'YouTube captions',
  websiteScreenshot__captureScreenshot: 'Website screenshot',

  // --- Unified Image ---
  unifiedImage__imageGenerationViaunifiedImage: 'Creating image',
  unifiedImage__imageEditViaunifiedImage: 'Editing image',

  // --- Virtual Filesystem ---
  VirtualFilesystem__losi__fs_list_files: 'List files',
  VirtualFilesystem__losi__fs_read_file: 'Read file',
  VirtualFilesystem__losi__fs_write_file: 'Wrote file',
  VirtualFilesystem__losi__fs_patch_file: 'Patched file',
  Sidecar__delegate: 'Delegate to Sidecar',
  BrowserExtensionHandoffTool__listDevices: 'Browser Sidecar devices',
  BrowserExtensionHandoffTool__handoffTask: 'Browser Sidecar',
  BrowserExtensionHandoffTool__getTaskStatus: 'Browser Sidecar status',

  // --- Audio / Video / 3D ---
  audioTool__processAudio: 'Audio',
  threeDAssetGenerator__generate3DAsset: 'Generate 3D asset',
  videoGenerator__videoGenerationViavideoGenerator: 'Create video',
  studioVideoGenerator__videoGenerationViastudioVideoGenerator: 'Studio video generation',

  // --- Video editing ---
  modifyVideoTool__modifyVideo: 'Edit video',

  // --- Automation ---
  taskAutomation__createTask: 'Create task',
  taskAutomation__listTasks: 'List tasks',
  taskAutomation__runTask: 'Run task',
  taskAutomation__scheduleTask: 'Schedule task',

  // --- Finance/Stocks ---
  yahooFinance__get_stock_data: 'Stock quote',

  // --- Slide & Presentation ---
  slideCreator__createPresentation: 'Create presentation',
  slideCreator__addTitleSlide: 'Add title slide',
  slideCreator__addContentSlide: 'Add content slide',
  slideCreator__addChartSlide: 'Add chart slide',
  slideCreator__addTableSlide: 'Add table slide',
  slideCreator__addImageSlide: 'Add image slide',
  slideCreator__exportPresentation: 'Export presentation',

  // --- Docs / Office ---
  documentGenerator__createDocument: 'Create document',
  documentGenerator__exportDocument: 'Export document',
  createPdfDocument__createPdfDocument: 'Create PDF Document',
  requestSignature__requestSignature: 'Request signature',
  spreadsheetTool__createSpreadsheet: 'Create spreadsheet',
  spreadsheetTool__updateSpreadsheet: 'Update spreadsheet',
  spreadsheetTool__exportSpreadsheet: 'Export spreadsheet',

  // --- Code / Dev ---
  codeInterpreter__run: 'Run code',
  codeExecutionTool__runCode: 'Run code',
  sandbox__runCode: 'Run code',
  sandbox__getWorkspace: 'Computer workspace',
  sandbox__runCommand: 'Computer command',
  sandbox__publishFiles: 'Computer downloads',
  sandbox__writeFiles: 'Computer files',
  sandbox__startPreview: 'Computer preview',
  sandbox__runAgentBrowser: 'Canvas browser',

  // --- GitHub ---
  github__list_repos: 'GitHub repos',
  github__get_repo: 'GitHub repo',
  github__list_branches: 'GitHub branches',
  github__list_issues: 'GitHub issues',
  github__get_issue: 'GitHub issue',
  github__create_issue: 'Create GitHub issue',
  github__update_issue: 'Update GitHub issue',
  github__comment_issue: 'Comment on GitHub issue',
  github__list_pull_requests: 'GitHub pull requests',
  github__get_pull_request: 'GitHub pull request',
  github__create_pull_request: 'Create pull request',
  github__merge_pull_request: 'Merge pull request',
  losiCode__check_coding_readiness: 'Check coding readiness',
  losiCode__request_connector: 'Request coding connector',
  losiCode__ensure_coding_task: 'Ensure coding task',
  losiCode__start_coding_run: 'Start coding run',
  losiCode__run_verification: 'Run verification',
  losiCode__get_coding_run_status: 'Coding run status',
  losiCode__merge_pull_request: 'Merge pull request',
  github__get_file: 'Read GitHub file',
  github__list_commits: 'GitHub commits',
  github__search_code: 'Search GitHub code',
  github__set_working_branch: 'Set working branch',
  github__create_or_update_file: 'Commit file',
  github__delete_file: 'Delete file',
  github__compare_refs: 'Compare refs',
  github__get_pull_request_diff: 'Pull request diff',

  // --- Data ---
  dataAnalysisTool__analyze: 'Analyze data',
  dataAnalysisTool__summarize: 'Summarize data',

  // --- Comms ---
  emailTool__sendEmail: 'Send email',
  smsTool__sendSms: 'Send text message',
  NexusCRMTool__run: 'Nexus Contacts',

  // --- Time / Misc ---
  currentTime__getCurrentTime: 'Current time',
  LosiSearch__searchEverything: 'Losi search',
  LosiUndo__listUndoableActions: 'Undoable changes',
  LosiUndo__undoAction: 'Undo change',
  CredentialVault__listCredentials: 'Credential vault',
  CredentialVault__requestLogin: 'Private vault login',
  CredentialVault__requestSecret: 'Private vault',
  computerUse__requestCredential: 'Private vault login',
  bookmarkSearchTool: 'Bookmark search',
  BookmarkSearch__searchBookmarks: 'Bookmark search',
  chatSearchTool: 'Chat search',
  ChatSearch__searchChats: 'Chat search',

  // --- Knowledge Graph ---
  knowledgeGraph__queryKnowledgeGraph: 'Search knowledge graph',
  knowledgeGraph__relatedItems: 'Find related items',

  // --- Agent Staffing (Nexus workforce) ---
  agentStaffing__listStaffingAgents: 'List staffing agents',
  agentStaffing__listStaffingTasks: 'List staffing tasks',
  agentStaffing__createStaffingTask: 'Create staffing task',
  agentStaffing__runStaffingTask: 'Run staffing task',
  agentStaffing__staffingTaskStatus: 'Staffing task status',

  // --- Losi Spaces ---
  losiSpaces__spaceTool: 'Spaces',
  losiSpaces__spaceTaskTool: 'Space tasks',
  losiSpaces__spaceNoteTool: 'Space notes',
  losiSpaces__spaceDesignTool: 'Space designs',
  losiSpaces__spaceSheetTool: 'Space sheets',
  losiSpaces__spaceFolderTool: 'Space folders',
  losiSpaces__spaceSprintTool: 'Space sprints',
  losiSpaces__spaceCalendarTool: 'Space calendars',
  losiSpaces__spaceContextTool: 'Space context',
  losiSpaces__spaceMemberTool: 'Space members',
  losiSpaces__spaceChatTool: 'Space chats',
  losiSpaces__spaceConversationTool: 'Space conversations',
  losiSpaces__spaceWhiteboardTool: 'Space whiteboards',
  losiSpaces__listSpaces: 'Spaces',
  losiSpaces__createSpace: 'Create Space',
  losiSpaces__updateSpace: 'Update Space',
  losiSpaces__archiveSpace: 'Archive Space',
  losiSpaces__listSpaceTasks: 'Tasks',
  losiSpaces__getSpaceTask: 'Task',
  losiSpaces__createSpaceTask: 'Create task',
  losiSpaces__updateSpaceTask: 'Update task',
  losiSpaces__deleteSpaceTask: 'Delete task',
  losiSpaces__listTaskSubtasks: 'Subtasks',
  losiSpaces__createTaskSubtask: 'Create subtask',
  losiSpaces__updateTaskSubtask: 'Update subtask',
  losiSpaces__deleteTaskSubtask: 'Delete subtask',
  losiSpaces__listTaskChecklistItems: 'Checklist',
  losiSpaces__createTaskChecklistItem: 'Create checklist item',
  losiSpaces__updateTaskChecklistItem: 'Update checklist item',
  losiSpaces__deleteTaskChecklistItem: 'Delete checklist item',
  losiSpaces__listTaskNotes: 'Task notes',
  losiSpaces__deleteTaskNote: 'Delete task note',
  losiSpaces__listTaskLinkedItems: 'Linked items',
  losiSpaces__searchTaskAttachableItems: 'Attachable items',
  losiSpaces__attachTaskLinkedItem: 'Attach item',
  losiSpaces__removeTaskLinkedItem: 'Remove item',
  losiSpaces__listSpaceNotes: 'Notes',
  losiSpaces__getSpaceNote: 'Note',
  losiSpaces__createSpaceNote: 'Create note',
  losiSpaces__updateSpaceNote: 'Update note',
  losiSpaces__deleteSpaceNote: 'Delete note',
  losiSpaces__listSpaceFolders: 'Folders',
  losiSpaces__getSpaceFolder: 'Folder',
  losiSpaces__createSpaceFolder: 'Create folder',
  losiSpaces__updateSpaceFolder: 'Update folder',
  losiSpaces__listSpaceSheets: 'Sheets',
  losiSpaces__createSpaceSheet: 'Create sheet',
  losiSpaces__updateSpaceSheet: 'Update sheet',
  losiSpaces__deleteSpaceSheet: 'Delete sheet',
  losiSpaces__replaceSpaceSheetWorkbook: 'Replace sheet workbook',
  losiSpaces__applySpaceSheetRangeUpdate: 'Update sheet range',
  losiSpaces__applySpaceSheetRangeFill: 'Fill sheet range',
  losiSpaces__applySpaceSheetRangeTransfer: 'Transfer sheet range',
  losiSpaces__applySpaceSheetStructureUpdate: 'Update sheet structure',
  losiSpaces__applySpaceSheetDimensionUpdate: 'Update sheet dimensions',
  losiSpaces__applySpaceSheetVisibilityUpdate: 'Update sheet visibility',
  losiSpaces__listSpaceSprints: 'Sprints',
  losiSpaces__getSpaceSprint: 'Sprint',
  losiSpaces__createSpaceSprint: 'Create sprint',
  losiSpaces__updateSpaceSprint: 'Update sprint',
  losiSpaces__listSpaceEvents: 'Events',
  losiSpaces__getSpaceEvent: 'Event',
  losiSpaces__listManagedCalendars: 'Managed calendars',
  losiSpaces__listManagedCalendarEvents: 'Managed calendar events',
  losiSpaces__createManagedCalendarEvent: 'Create managed event',
  losiSpaces__updateManagedCalendarEvent: 'Update managed event',
  losiSpaces__deleteManagedCalendarEvent: 'Delete managed event',
  losiSpaces__checkManagedCalendarAvailability: 'Check calendar availability',
  losiSpaces__createSpaceEvent: 'Create event',
  losiSpaces__updateSpaceEvent: 'Update event',
  losiSpaces__deleteSpaceEvent: 'Remove event',
  losiSpaces__listSpaceFiles: 'Files',
  losiSpaces__getSpaceFile: 'File',
  losiSpaces__searchSpaceFiles: 'Search files',
  losiSpaces__attachSpaceFile: 'Attach file',
  losiSpaces__updateSpaceFile: 'Update file',
  losiSpaces__detachSpaceFile: 'Detach file',
  losiSpaces__listSpaceCollections: 'Collections',
  losiSpaces__getSpaceCollection: 'Collection',
  losiSpaces__attachSpaceCollection: 'Attach collection',
  losiSpaces__updateSpaceCollection: 'Update collection',
  losiSpaces__detachSpaceCollection: 'Detach collection',
  losiSpaces__listSpaceMembers: 'Members',
  losiSpaces__listSpaceMemberDirectory: 'Member directory',
  losiSpaces__addSpaceMember: 'Add member',
  losiSpaces__inviteToSpace: 'Invite to Space',
  losiSpaces__updateSpaceMemberRole: 'Update member role',
  losiSpaces__removeSpaceMember: 'Remove member',
  losiSpaces__listSpaceChats: 'Chats',
  losiSpaces__getSpaceChat: 'Chat',
  losiSpaces__linkSpaceChat: 'Link chat',
  losiSpaces__moveSpaceChat: 'Move chat',
  losiSpaces__unlinkSpaceChat: 'Unlink chat',
  losiSpaces__listSpaceConversations: 'Conversations',
  losiSpaces__startSpaceConversation: 'Create conversation',
  losiSpaces__getSpaceConversation: 'Conversation',
  losiSpaces__updateSpaceConversation: 'Update conversation',
  losiSpaces__archiveSpaceConversation: 'Archive conversation',
  losiSpaces__duplicateSpaceConversation: 'Duplicate conversation',
  losiSpaces__listSpaceConversationMessages: 'Conversation messages',
  losiSpaces__respondSpaceConversation: 'Conversation reply',
  losiSpaces__listSpaceWhiteboards: 'Whiteboards',
  losiSpaces__getSpaceWhiteboard: 'Whiteboard',
  losiSpaces__createSpaceWhiteboard: 'Create whiteboard',
  losiSpaces__updateSpaceWhiteboard: 'Update whiteboard',
  losiSpaces__listSpaceDesigns: 'Designs',
  losiSpaces__getSpaceDesign: 'Design',
  losiSpaces__createSpaceDesign: 'Create design',
  losiSpaces__updateSpaceDesign: 'Update design',
  losiSpaces__deleteSpaceDesign: 'Delete design',
  losiSpaces__applySpaceDesignSceneMutation: 'Update design scene',
  losiSpaces__deleteSpaceWhiteboard: 'Delete whiteboard',

  // --- Generated integrations ---
  GeneratedIntegrationTool__draftIntegration: 'Draft integration',
  GeneratedIntegrationTool__approveIntegrationDraft: 'Create integration',
  GeneratedIntegrationTool__updateIntegration: 'Update integration',
  GeneratedIntegrationTool__deleteIntegration: 'Delete integration',
  MCPIntegrationTool__searchMcpServers: 'Search MCP servers',
  MCPIntegrationTool__installMcpServer: 'Add MCP server',
  MCPIntegrationTool__listMcpServerTools: 'List MCP tools',
  MCPIntegrationTool__callMcpServerTool: 'Use MCP tool',
  SkillIntegrationTool__searchSkills: 'Search skills',
  SkillIntegrationTool__installSkill: 'Install skill',
  SkillIntegrationTool__createSkill: 'Create skill',
  SkillIntegrationTool__enableSkill: 'Enable skill',
  SkillIntegrationTool__updateSkill: 'Update skill',
  SkillIntegrationTool__listInstalledSkills: 'List skills',
  SkillIntegrationTool__getSkillInstructions: 'Use skill',
  SkillIntegrationTool__runSkill: 'Run skill',

  // --- Content Engine ---
  ContentEngine__createCampaignDraft: 'Create campaign draft',
  ContentEngine__approveCampaignAndCreateAssetPack: 'Create asset pack',
  ContentEngine__approveAssetPackAndGenerateVisuals: 'Generate visuals',
  ContentEngine__linkResultToSpace: 'Link Engine output',
  ContentEngine__getContext: 'Content Engine context',
  ContentEngine__updateCampaign: 'Update campaign',

  // --- Losi Super Agents ---
  LosiProductCreation__createSite: 'Creating Site',
  LosiProductCreation__createEngine: 'Creating Engine',

  // --- Live Scores ---
  liveScores__getLiveScores: 'Live scores',
  liveScores__watchGame: 'Live score card'

  // --- Generic ---
  // Add more mappings here as new tools or function ids appear.
}
